import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Modal,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

const MODAL_TITLE_ID = 'upcoming-assignments-info-modal-title';

const STRINGS = {
  title: 'Assignments',
  close: 'Close',
  items: [
    {
      id: 'upcoming',
      title: 'Upcoming assignment',
      description:
        'The color coding highlights upcoming assignment nearest to the due date.',
    },
    {
      id: 'open',
      title: 'Open assignment',
      description: 'The color coding highlights assignments which are open.',
    },
    {
      id: 'hidden',
      title: 'Hidden assignment',
      description: 'The icon highlights hidden assignments to educators.',
    },
  ],
};

type UpcomingAssignmentsInfoModalProps = {
  open: boolean;
  onClose: () => void;
};

type LegendRowProps = {
  children: React.ReactNode;
  title: string;
  description: string;
};

const LegendRow = ({ children, title, description }: LegendRowProps) => (
  <Box display="flex" gap="5px" alignItems="flex-start">
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={24}
      minWidth={24}
      pt={0.25}
      tabIndex={0}
      aria-label={title}
    >
      {children}
    </Box>
    <Box flex={1}>
      <Typography
        variant="subtitle1"
        fontWeight={500}
        fontSize="16px"
        color="text.primary"
        tabIndex={0}
        sx={{ lineHeight: '150%', letterSpacing: '0.15px' }}
        aria-label={title}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        fontWeight={400}
        sx={{ lineHeight: '180%', letterSpacing: '0.17px' }}
        mt={0.5}
        tabIndex={0}
        aria-label={description}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

const UpcomingAssignmentsInfoModal = ({
  open,
  onClose,
}: UpcomingAssignmentsInfoModalProps) => {
  const { palette } = useTheme();

  return (
    <Modal open={open} onClose={onClose} aria-labelledby={MODAL_TITLE_ID}>
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby={MODAL_TITLE_ID}
        sx={{
          width: { xs: 'calc(100vw - 32px)', sm: 560 },
          height: { sm: 363 },
          bgcolor: palette.background.paper,
          borderRadius: '12px',
          boxShadow: 24,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          px={{ xs: 2, sm: 2 }}
          pt={3}
          pb={2}
        >
          <Typography
            id={MODAL_TITLE_ID}
            variant="subtitle1"
            color={palette.primary.main}
            tabIndex={0}
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '150%',
              letterSpacing: '0.15px',
            }}
          >
            {STRINGS.title}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close assignments information dialog"
            sx={{
              mt: '8px',
              mr: '3px',
              color: palette.action.active,
              width: '14px',
              height: '14px',
            }}
          >
            <Icons.Close />
          </IconButton>
        </Box>

        <Box
          px={{ xs: 2, sm: 2 }}
          pb={2}
          display="flex"
          flexDirection="column"
          gap={2.5}
        >
          <LegendRow
            title={STRINGS.items[0].title}
            description={STRINGS.items[0].description}
          >
            <Box
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: palette.warning.light,
                mt: '5px',
              }}
            />
          </LegendRow>

          <LegendRow
            title={STRINGS.items[1].title}
            description={STRINGS.items[1].description}
          >
            <Box
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: palette.info.light,
                mt: '5px',
              }}
            />
          </LegendRow>

          <LegendRow
            title={STRINGS.items[2].title}
            description={STRINGS.items[2].description}
          >
            <VisibilityOffOutlinedIcon
              sx={{ fontSize: 20, color: palette.text.disabled }}
              aria-hidden
            />
          </LegendRow>
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          px={{ xs: 2, sm: 2 }}
          pb={3}
          pt={1}
        >
          <Button kind="primary" onClick={onClose}>
            {STRINGS.close}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpcomingAssignmentsInfoModal;
